/**
 * Expansion Gate Component
 * Paragraph expansion from seed sentence with vocabulary analysis feedback
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpansionQuestion, GateEvaluationResult, VocabularyAnalysis } from '../../types/writing';
import { ClickableText } from '../ClickableWord';
import FeedbackModal from './FeedbackModal';
import './GateComponents.css';

interface ExpansionGateProps {
  question: ExpansionQuestion;
  onSubmit: (answer: string) => void;
  submitting: boolean;
  result: GateEvaluationResult | null;
  vocabularyAnalysis?: VocabularyAnalysis;
  onSaveToNotes?: () => void;
  onSaveItemToNotes?: (item: string) => void;
  onFamiliarityUpdate?: () => void;
}

const ExpansionGate: React.FC<ExpansionGateProps> = ({
  question,
  onSubmit,
  submitting,
  result,
  vocabularyAnalysis,
  onSaveToNotes,
  onSaveItemToNotes,
  onFamiliarityUpdate,
}) => {
  const { t } = useTranslation(['writing', 'common']);
  const [answer, setAnswer] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Reset answer when question changes
  useEffect(() => {
    setAnswer('');
  }, [question.id]);

  // Show feedback modal when result is first received
  useEffect(() => {
    if (result) {
      setShowFeedbackModal(true);
    }
  }, [result]);

  // Count sentences in the answer
  const sentenceCount = useMemo(() => {
    if (!answer.trim()) return 0;
    // Simple sentence counting: split by . ! ?
    const sentences = answer.split(/[.!?]+/).filter(s => s.trim().length > 0);
    return sentences.length;
  }, [answer]);

  // Check which required vocabulary words are used
  const usedVocabulary = useMemo(() => {
    const lowerAnswer = answer.toLowerCase();
    return question.requiredVocabulary.map(word => ({
      word,
      used: lowerAnswer.includes(word.toLowerCase()),
    }));
  }, [answer, question.requiredVocabulary]);

  const allVocabUsed = usedVocabulary.every(v => v.used);
  const minSentencesMet = sentenceCount >= question.minSentences;

  const handleSubmit = () => {
    if (answer.trim() && !submitting) {
      onSubmit(answer.trim());
    }
  };

  return (
    <div className="gate-question expansion-gate">
      {/* Question */}
      <div className="gate-question__card">
        <div className="gate-question__header">
          <span className="gate-question__type">{t('paragraphExpansion')}</span>
        </div>

        <div className="gate-question__content">
          <p className="expansion-instruction">
            {t('expansionInstructions')}
          </p>

          {/* Seed sentence */}
          <div className="expansion-seed">
            <span className="expansion-seed__label">{t('seedSentence')}:</span>
            <p className="expansion-seed__text">"<ClickableText text={question.seedSentence} />"</p>
          </div>

          {/* Theme (if any) */}
          {question.theme && (
            <div className="expansion-theme">
              <span className="expansion-theme__label">{t('theme')}:</span>
              <span className="expansion-theme__text"><ClickableText text={question.theme} /></span>
            </div>
          )}

          {/* Required vocabulary */}
          <div className="expansion-vocab">
            <span className="expansion-vocab__label">{t('requiredVocabulary')}:</span>
            <div className="expansion-vocab__list">
              {usedVocabulary.map(({ word, used }, index) => (
                <span
                  key={index}
                  className={`expansion-vocab__word ${used ? 'used' : ''} ${result ? '' : ''}`}
                >
                  <ClickableText text={word} />
                  {used && <span className="expansion-vocab__check">✓</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Requirements */}
          <div className="expansion-requirements">
            <span className={`expansion-requirement ${minSentencesMet ? 'met' : ''}`}>
              {minSentencesMet ? '✓' : '○'} {t('minSentences', { count: question.minSentences })}
              <span className="expansion-requirement__current">
                ({sentenceCount}/{question.minSentences})
              </span>
            </span>
            <span className={`expansion-requirement ${allVocabUsed ? 'met' : ''}`}>
              {allVocabUsed ? '✓' : '○'} {t('useAllVocabulary')}
            </span>
          </div>

          {question.translation && (
            <p className="gate-question__translation">{question.translation}</p>
          )}
        </div>

        {/* Answer input */}
        {!result && (
          <div className="expansion-input-container">
            <textarea
              className="expansion-input"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={t('writeYourParagraph')}
              disabled={submitting}
              rows={6}
              autoFocus
            />
            <div className="expansion-input-stats">
              <span className="expansion-input-stat">
                {t('sentences')}: {sentenceCount}
              </span>
              <span className="expansion-input-stat">
                {t('words')}: {answer.trim() ? answer.trim().split(/\s+/).length : 0}
              </span>
            </div>
          </div>
        )}

        {/* Submitted answer display */}
        {result && (
          <div className="expansion-submitted">
            <span className="expansion-submitted__label">{t('yourParagraph')}:</span>
            <p className="expansion-submitted__text">{answer}</p>
          </div>
        )}
      </div>

      {/* Result Summary (compact view) */}
      {result && (
        <div className={`gate-feedback gate-feedback--compact ${result.passed ? 'pass' : 'fail'}`}>
          <div className="gate-feedback__header">
            <span className="gate-feedback__status">
              {result.passed ? '✓ ' + t('wellDone') : '✗ ' + t('needsImprovement')}
            </span>
            <span className="gate-feedback__score">{result.score}%</span>
          </div>
          <p className="gate-feedback__text"><ClickableText text={result.feedback} /></p>

          {/* Vocabulary progress summary */}
          {vocabularyAnalysis && vocabularyAnalysis.totalFamiliarityGain > 0 && (
            <div className="gate-feedback__vocab-summary">
              <span className="vocab-progress-badge">
                +{vocabularyAnalysis.totalFamiliarityGain} {t('familiarityPoints')}
              </span>
              {vocabularyAnalysis.familiarityChanges.filter(c => c.isNew).length > 0 && (
                <span className="new-words-badge">
                  {vocabularyAnalysis.familiarityChanges.filter(c => c.isNew).length} {t('newWordsLearned')}
                </span>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="gate-feedback__btn-row">
            <button
              className="gate-feedback__view-details-btn"
              onClick={() => setShowFeedbackModal(true)}
            >
              {t('viewDetailedFeedback')}
            </button>
            {onSaveToNotes && (
              <button className="gate-feedback__save-note-btn" onClick={onSaveToNotes}>
                📝 {t('saveToNotes')}
              </button>
            )}
          </div>

          {/* Quick actions */}
          <div className="gate-feedback__actions">
            {result.grammarIssues && result.grammarIssues.length > 0 && (
              <span className="action-badge">{result.grammarIssues.length} {t('grammarIssues')}</span>
            )}
            {result.suggestions && result.suggestions.length > 0 && (
              <span className="action-badge">{result.suggestions.length} {t('suggestions')}</span>
            )}
          </div>
        </div>
      )}

      {/* Submit Button */}
      {!result && (
        <button
          className="gate-submit-btn"
          onClick={handleSubmit}
          disabled={!answer.trim() || submitting}
        >
          {submitting ? t('submitting') : t('submit')}
        </button>
      )}

      {/* Feedback Modal */}
      {result && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          result={result}
          vocabularyAnalysis={vocabularyAnalysis}
          onSaveToNotes={onSaveItemToNotes}
          onFamiliarityUpdateComplete={onFamiliarityUpdate}
        />
      )}
    </div>
  );
};

export default ExpansionGate;
